import React, { useCallback, useEffect, useState } from 'react';
import { RootState } from 'shared/store/rootReducer';
import { archiveModalToggleView } from 'shared/store/features/archiveWorkOrdersModalSlice';
import { updateArchiveStatusOfMany } from 'shared/store/features/workOrdersSlice';
import { setSelectedWorkOrders } from 'shared/store/features/workOrdersTableSlice';
import { closeAlert, setAlert } from 'shared/store/features/alertSlice';
import { useDispatch, useSelector } from 'react-redux';
import { archiveWorkOrders } from 'shared/api/workOrders';
import WorkOrder from 'shared/interfaces/WorkOrder.interface';
import { setArchiveFilterStatus } from 'shared/store/features/archiveFilterStatusSlice';
import { ArchiveStatus } from '../filterOptions.enum';

interface ArchivedWorkOrders {
  ids: string[],
  reasonOfArchiving: string
}

const useArchiveWorkOrderModalLogic = () => {
  const dispatch = useDispatch();
  const { isArchiveWorkOrdersModalOpened } = useSelector(
    (state: RootState) => state.archiveWorkOrdersModal
  );
  const { selectedWorkOrders } = useSelector(
    (state: RootState) => state.workOrdersTable
  );
  const [isLoading, setIsLoading] = useState(false);
  const [reasonOfArchiving, setReasonOfArchiving] = useState('');
  const [activeArchiveButton, setActiveArchiveButton] = useState(false);
  const [workOrdersToBeArchived, setWorkOrdersToBeArchived] = useState<ArchivedWorkOrders>({
    ids: [],
    reasonOfArchiving: ''
  });

  const onClose = () => { dispatch(archiveModalToggleView({ isArchiveWorkOrdersModalOpened: false })); setReasonOfArchiving(''); };

  const onReasonInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonOfArchiving(event.target.value);
  };

  const activeArchiveButtonHandler = useCallback(() => {
    setActiveArchiveButton(Boolean(reasonOfArchiving));
  }, [reasonOfArchiving]);

  useEffect(() => {
    activeArchiveButtonHandler();
  }, [activeArchiveButtonHandler]);

  const updateWorkOrders = useCallback(() => {
    const ids = selectedWorkOrders.map(WO => (WO.id));
    setWorkOrdersToBeArchived({ ids, reasonOfArchiving });
  }, [reasonOfArchiving, selectedWorkOrders]);

  useEffect(() => {
    updateWorkOrders();
  }, [updateWorkOrders]);

  const onArchiveSubmit = () => {
    setIsLoading(true);
    archiveWorkOrders(workOrdersToBeArchived.ids, workOrdersToBeArchived.reasonOfArchiving)
      .then((data: WorkOrder[]) => {
        dispatch(updateArchiveStatusOfMany({
          isArchived: true,
          ids: data.map(wo => wo.id),
          archiveReason: workOrdersToBeArchived.reasonOfArchiving
        }));
        dispatch(setAlert({
          text: 'Work order was archived',
          variant: 'success',
          open: true,
          onClickButton: () => {
            dispatch(setArchiveFilterStatus({ archiveFilterStatus: ArchiveStatus.archived }));
            dispatch(closeAlert());
          },
          onClickButtonLabel: 'Show Archived Work Orders'
        })); 
        setReasonOfArchiving('');
      })
      .catch((error: any) => dispatch(setAlert({
        text: error.message,
        variant: 'success',
        open: true
      })))
      .finally(() => {
        setIsLoading(false);
        dispatch(archiveModalToggleView({ isArchiveWorkOrdersModalOpened: false }));
        dispatch(setSelectedWorkOrders({ selectedWorkOrders: [] }));
      });
  };

  return {
    activeArchiveButton,
    reasonOfArchiving,
    onArchiveSubmit,
    onReasonInputChange,
    setReasonOfArchiving,
    activeArchiveButtonHandler,
    isArchiveWorkOrdersModalOpened,
    onClose,
    isLoading,
    workOrdersToBeArchived
  };
};

export default useArchiveWorkOrderModalLogic;
